import { DepartmentLabel, useDepartmentText } from 'model/DepartmentTerminology';
import { Badge, Box, Group, SimpleGrid, Stack, Textarea, TextInput, Tooltip } from '@mantine/core';
import { IconCheck, IconPlus, IconTree } from '@tabler/icons-react';
import { isEmpty, noop } from 'lodash';
import { FormProps } from 'components/forms/FormProps';
import useLoadingAction from 'hooks/use-loading-action';
import { useForm } from '@mantine/form';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useMemo, useState } from 'react';
import PickDepartments, { PickDepartmentItem } from 'components/inputs/multi-checkbox/PickDepartments';
import FormWrapper from 'components/forms/FormWrapper';
import FormInputGroup from 'components/forms/FormInputGroup';
import { DEVICE_TYPE_MAP } from 'model/DeviceType';
import DeviceTypeIcon from 'components/device/DeviceTypeIcon';
import H3Medium from 'components/typography/H3Medium';
import P1Regular from 'components/typography/P1Regular';
import PickOrganizations, { PickOrganizationItem } from 'components/inputs/multi-checkbox/PickOrganizations';
import P1Medium from 'components/typography/P1Medium';
import ThreeWaySelect, { SelectedEntityRow } from 'components/forms/device-subtype/ThreeWaySelect';
import P2Regular from 'components/typography/P2Regular';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';
import DepartmentParentReference from 'components/department/DepartmentParentReference';
import { DepartmentBreadcrumbItem, formatDepartmentPath } from 'components/department/DepartmentBreadcrumb';
import P3Regular from 'components/typography/P3Regular';

const SUBTREE_TOOLTIP_ITEM_LIMIT = 20;

type DeviceSubtypeDepartment = {
  departmentId: number;
  departmentName: string;
  breadcrumbs?: DepartmentBreadcrumbItem[];
};

/** Badge describing the selected object's descendants. */
function DepartmentSubtreeBadge({
  department,
  descendants,
}: {
  department: DeviceSubtypeDepartment;
  descendants: PickDepartmentItem[];
}) {
  const departmentText = useDepartmentText();
  if (descendants.length === 0) {
    return null;
  }

  const departmentDepth = department.breadcrumbs?.length ?? 1;
  const visibleDescendants = descendants.slice(0, SUBTREE_TOOLTIP_ITEM_LIMIT);
  const remainingCount = descendants.length - visibleDescendants.length;

  return (
    <Tooltip
      label={
        <Stack spacing={4}>
          {visibleDescendants.map(({ departmentId, departmentName, breadcrumbs }) => {
            const relativeDepth = breadcrumbs.length - departmentDepth;

            return (
              <P3Regular
                key={departmentId}
                data-subtree-depth={relativeDepth}
                style={{ paddingLeft: Math.max(0, relativeDepth - 1) * 16 }}
              >
                {departmentName}
              </P3Regular>
            );
          })}
          {remainingCount > 0 && <P3Regular>(+{remainingCount} viac)</P3Regular>}
        </Stack>
      }
      events={{ hover: true, focus: true, touch: true }}
      multiline
      withArrow
      withinPortal
    >
      <Badge
        aria-label={departmentText(
          `Podstrom položky úrovne ${department.departmentName}: ${descendants.length}`,
          `Podstrom strediska ${department.departmentName}: ${descendants.length}`
        )}
        size="sm"
        tabIndex={0}
      >
        <Group spacing={4} noWrap>
          <IconTree aria-hidden size={14} stroke={1.8} />
          {descendants.length}
        </Group>
      </Badge>
    </Tooltip>
  );
}

export interface DeviceSubtypeFormData {
  parentDeviceTypeId: number;
  deviceSubtypeName: string;
  deviceSubtypeShortName?: string;
  description?: string;
  restriction: 'global' | 'department' | 'organization';
  departments: DeviceSubtypeDepartment[];
  organizations:
    | PickOrganizationItem[]
    | {
        organizationId: number;
        organizationName: string;
      }[];
}

export const deviceSubtypeFormInitialValues = {
  parentDeviceTypeId: 0,
  deviceSubtypeName: '',
  deviceSubtypeShortName: '',
  restriction: 'department' as const,
  departments: [],
  organizations: [],
};

/**
 * Parameters of the OrganizationForm.
 */
export interface DeviceSubtypeFormProps extends FormProps<DeviceSubtypeFormData> {
  readOnly?: Partial<Record<keyof DeviceSubtypeFormData, boolean>>;
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
}

/**
 * Form for creating and editing device subtypes.
 */
export default function DeviceSubtypeForm({
  initialValues = deviceSubtypeFormInitialValues,
  onSubmit = noop,
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke="1.5" size={24} />,
}: DeviceSubtypeFormProps) {
  const departmentText = useDepartmentText();
  const { confirm } = useConfirm();
  const { isTablet } = useResponsiveDesign();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const [openedDepartments, { close: closeDepartments, open: openDepartments }] = useDisclosure(false);
  const [openedOrganizations, { close: closeOrganizations, open: openOrganizations }] = useDisclosure(false);
  const [availableDepartments, setAvailableDepartments] = useState<PickDepartmentItem[]>([]);

  const form = useForm({
    initialValues,
    validate: {
      deviceSubtypeName: createValidator([required]),
      organizations: (value) => {
        if (form.values.restriction === 'organization' && isEmpty(value)) {
          return 'Vyberte aspoň jednu organizáciu';
        }

        return null;
      },
      departments: (value) => {
        if (form.values.restriction === 'department' && isEmpty(value)) {
          return (
            <DepartmentLabel
              objectLabel="Vyberte aspoň jednu položku úrovne"
              departmentLabel="Vyberte aspoň jedno stredisko"
            />
          );
        }

        return null;
      },
    },
  });

  useDirtyFormHandler(form);

  const sectionTitle = useMemo(
    () => (
      <Group noWrap spacing={8}>
        <DeviceTypeIcon typeId={form.values.parentDeviceTypeId} size="md" />
        <H3Medium color="gray.7">{DEVICE_TYPE_MAP.get(form.values.parentDeviceTypeId)?.name}</H3Medium>
      </Group>
    ),
    [form.values.parentDeviceTypeId, DEVICE_TYPE_MAP]
  );

  /**
   * Confirms the removal of the organization from the list of selected organizations.
   */
  const confirmRemoveOrganization = useCallback(
    (organizationName: string, index: number) => {
      confirm({
        title: 'Odstrániť organizáciu',
        content: `Naozaj chcete odstrániť organizáciu ${organizationName} zo zoznamu?`,
        onConfirm: () => form.removeListItem('organizations', index),
      });
    },
    [confirm]
  );

  /**
   * Confirms the removal of the department from the list of selected departments.
   */
  const confirmRemoveDepartment = useCallback(
    (departmentName: string, index: number) => {
      confirm({
        title: <DepartmentLabel objectLabel="Odstrániť položku úrovne" departmentLabel="Odstrániť stredisko" />,
        content: (
          <DepartmentLabel
            objectLabel={`Naozaj chcete odstrániť položku úrovne ${departmentName} zo zoznamu?`}
            departmentLabel={`Naozaj chcete odstrániť stredisko ${departmentName} zo zoznamu?`}
          />
        ),
        onConfirm: () => form.removeListItem('departments', index),
      });
    },
    [confirm]
  );

  const addOrganizationButton = useMemo(
    () => (
      <Box>
        <Button onClick={openOrganizations} variant="subtle" leftIcon={<IconPlus />}>
          Pridať organizácie
        </Button>
      </Box>
    ),
    [openOrganizations]
  );

  const addDepartmentButton = useMemo(
    () => (
      <Box>
        <Button onClick={openDepartments} variant="subtle" leftIcon={<IconPlus />}>
          {departmentText('Pridať položky úrovne', 'Pridať strediská')}
        </Button>
      </Box>
    ),
    [departmentText, openDepartments]
  );

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle={sectionTitle}>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            label="Názov typu zariadenia"
            placeholder="Názov typu zariadenia"
            size="lg"
            name="objekto-deviceSubtypeName"
            {...form.getInputProps('deviceSubtypeName')}
          />
          <TextInput
            label="Krátky názov typu zariadenia"
            placeholder="Krátky názov typu zariadenia"
            size="lg"
            name="objekto-deviceSubtypeShortName"
            {...form.getInputProps('deviceSubtypeShortName')}
          />
        </SimpleGrid>

        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <Textarea
            label="Popis typu zariadenia"
            placeholder="Popis typu zariadenia"
            size="lg"
            name="objekto-description"
            {...form.getInputProps('description')}
          />
        </SimpleGrid>
      </FormInputGroup>
      <FormInputGroup groupTitle="Vymedzenie pre:">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <ThreeWaySelect {...form.getInputProps('restriction')} permissionSlug="define-device-subtypes" />
        </SimpleGrid>
        {form.values.restriction === 'global' && (
          <P1Regular color="gray.6">
            {departmentText(
              'Typ zariadenia bude dostupný na všetkých položkách úrovne.',
              'Typ zariadenia bude dostupný na všetkých strediskách.'
            )}
          </P1Regular>
        )}
        {form.values.restriction === 'organization' &&
          (isEmpty(form.values.organizations) ? (
            <Stack align="flex-start">
              <P1Regular color="gray.6">
                {departmentText(
                  'Zatiaľ nie sú pridané žiadne organizácie. Tento typ zariadenia bude dostupný na všetkých položkách úrovne vybraných organizácií.',
                  'Zatiaľ nie sú pridané žiadne organizácie. Tento typ zariadenia bude dostupný na všetkých strediskách vybraných organizácií.'
                )}
              </P1Regular>
              <P2Regular color="red.8">{form.errors.organizations}</P2Regular>
              {addOrganizationButton}
            </Stack>
          ) : (
            <Stack pt={10} spacing={24}>
              <Box>
                <Box bg="gray.0" style={{ borderRadius: '4px' }} p={16}>
                  <P1Medium color="gray.8">Organizácia</P1Medium>
                </Box>

                {form.values.organizations.map((organization, index) => (
                  <SelectedEntityRow
                    key={organization.organizationId}
                    name={organization.organizationName}
                    deleteText="Zmazať organizáciu"
                    onDelete={() => {
                      confirmRemoveOrganization(organization.organizationName, index);
                    }}
                  />
                ))}
              </Box>

              {addOrganizationButton}
            </Stack>
          ))}
        {form.values.restriction === 'department' &&
          (isEmpty(form.values.departments) ? (
            <Stack align="flex-start">
              <P1Regular color="gray.6">
                {departmentText(
                  'Zatiaľ nie sú pridané žiadne položky úrovne. Tento typ zariadenia bude dostupný len pre vybrané položky úrovne.',
                  'Zatiaľ nie sú pridané žiadne strediská. Tento typ zariadenia bude dostupný len pre vybrané strediská.'
                )}
              </P1Regular>
              <P2Regular color="red.8">{form.errors.departments}</P2Regular>
              {addDepartmentButton}
            </Stack>
          ) : (
            <Stack pt={10} spacing={24}>
              <Box>
                <Box bg="gray.0" style={{ borderRadius: '4px' }} p={16}>
                  <P1Medium color="gray.8">{departmentText('Položka úrovne', 'Stredisko')}</P1Medium>
                </Box>

                {form.values.departments.map((department, index) => {
                  const descendants = availableDepartments
                    .filter(({ breadcrumbs }) =>
                      breadcrumbs
                        .slice(0, -1)
                        .some(({ departmentId: ancestorId }) => ancestorId === department.departmentId)
                    )
                    .sort((left, right) =>
                      formatDepartmentPath(left.breadcrumbs).localeCompare(
                        formatDepartmentPath(right.breadcrumbs),
                        'sk'
                      )
                    );

                  return (
                    <Box
                      key={department.departmentId}
                      data-testid={`selected-department-${department.departmentId}`}
                      data-selected-department-id={department.departmentId}
                      data-hierarchy-depth={0}
                    >
                      <SelectedEntityRow
                        name={department.departmentName}
                        leftSection={
                          <DepartmentParentReference breadcrumbs={department.breadcrumbs ?? []} showFullPath />
                        }
                        rightSection={<DepartmentSubtreeBadge department={department} descendants={descendants} />}
                        deleteText={departmentText('Zmazať položku úrovne', 'Zmazať stredisko')}
                        onDelete={() => {
                          confirmRemoveDepartment(department.departmentName, index);
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              {addDepartmentButton}
            </Stack>
          ))}
      </FormInputGroup>

      <PickOrganizations
        opened={openedOrganizations}
        onClose={closeOrganizations}
        permissionSlug="define-device-subtypes"
        onPick={(organizations) => {
          for (const item of organizations) {
            form.insertListItem('organizations', item);
          }
        }}
        excludeIds={form.values.organizations.map(({ organizationId }) => organizationId)}
      />

      <PickDepartments
        opened={openedDepartments}
        onClose={closeDepartments}
        permissionSlug="define-device-subtypes"
        onPick={(departments) => {
          for (const item of departments) {
            form.insertListItem('departments', item);
          }
        }}
        onLoad={setAvailableDepartments}
        excludeIds={form.values.departments.map(({ departmentId }) => departmentId)}
      />
    </FormWrapper>
  );
}
